import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Card, Pill, Section } from '../_primitives';
import { ScoreGauge } from '../ScoreGauge';

export interface QualitySubScore {
  label: string;
  score: number;
}

export interface QualityFinding {
  text: string;
  /** When set, renders the finding in the warning color. */
  warning?: boolean;
}

export interface ContentQualityCardProps {
  overallScore: number;
  grade?: string | null;
  /** Optional delta vs previous analysis, e.g. "+5 since last analysis". */
  delta?: string | null;
  pills?: QualitySubScore[];
  /** Sections render only when real data exists for them — no placeholder scores. */
  seo?: { score: number; findings: QualityFinding[] } | null;
  authority?: { score: number; findings: QualityFinding[] } | null;
  structure?: { score: number; findings: QualityFinding[] } | null;
  judge?: { score: number; summary: ReactNode } | null;
}

/**
 * Quality summary with score gauge + 4 collapsible sections (SEO, Authority,
 * Structure, Judge). Listens for the `ed-focus-quality` window event so the
 * top-bar score badge can scroll to and pulse this card.
 */
export function ContentQualityCard({
  overallScore,
  grade = null,
  delta,
  pills,
  seo,
  authority,
  structure,
  judge,
}: ContentQualityCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const onFocus = () => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1400);
      return () => clearTimeout(t);
    };
    window.addEventListener('ed-focus-quality', onFocus);
    return () => window.removeEventListener('ed-focus-quality', onFocus);
  }, []);

  return (
    <div
      ref={cardRef}
      data-rail-section="quality"
      className={cn(
        'bg-card border border-border rounded-lg overflow-hidden transition-shadow',
        pulse && 'ring-2 ring-coral ring-offset-2 ring-offset-coral-light',
      )}
    >
      <div className="px-3 pt-3.5 pb-2 flex items-center gap-3">
        <ScoreGauge value={overallScore} grade={grade} size={92} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold text-foreground">Content Quality</div>
          {delta && (
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{delta}</div>
          )}
          {pills && pills.length > 0 && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {pills.map((p) => (
                <Pill
                  key={p.label}
                  tone={p.score >= 80 ? 'success' : p.score >= 60 ? 'warning' : 'danger'}
                  size="sm"
                >
                  {p.label} {p.score}
                </Pill>
              ))}
            </div>
          )}
        </div>
      </div>

      {seo && (
        <Section title="SEO" hint="Keywords + intent" score={seo.score}>
          <FindingList items={seo.findings} />
        </Section>
      )}
      {authority && (
        <Section title="Authority" hint="Citations + trust" score={authority.score}>
          <FindingList items={authority.findings} />
        </Section>
      )}
      {structure && (
        <Section title="Structure" hint="Headings + scannability" score={structure.score} defaultOpen={false}>
          <FindingList items={structure.findings} />
        </Section>
      )}
      {judge && (
        <Section title="Judge" hint="AI quality grade" score={judge.score} defaultOpen={false}>
          <div className="text-[11.5px] text-muted-foreground leading-relaxed">{judge.summary}</div>
        </Section>
      )}
    </div>
  );
}

function FindingList({ items }: { items: QualityFinding[] }) {
  if (items.length === 0) {
    return <div className="text-[11.5px] text-muted-foreground italic">No findings.</div>;
  }
  return (
    <ul className="m-0 pl-4 text-[11.5px] leading-relaxed list-disc marker:text-muted-foreground">
      {items.map((f, i) => (
        <li
          key={i}
          className={cn(
            f.warning ? 'text-[hsl(var(--status-warning))]' : 'text-muted-foreground',
          )}
        >
          {f.text}
        </li>
      ))}
    </ul>
  );
}
